import { appConfig } from './../config'
import {
  Material,
  Mesh,
  PerspectiveCamera,
  PlaneGeometry,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three'
import { v4 } from 'uuid'
import { ConfigsType } from '../config'
import {
  DividerPos,
  SectionPos,
  SelectedSection,
} from './../components/Three/Three'
import { createDivider } from './3DObjects'
import Section, { DividerPropsType, SectionProps } from './Section'

const getScreenPos = (
  pos: Vector3,
  camera: PerspectiveCamera,
  width: number,
  height: number
) => {
  const widthHalf = width / 2
  const heightHalf = height / 2

  pos.project(camera)
  pos.x = pos.x * widthHalf + widthHalf
  pos.y = -(pos.y * heightHalf) + heightHalf

  return {
    top: pos.y,
    left: pos.x,
  }
}

export default class Selecting {
  raycater = new Raycaster()
  mouse = new Vector2()

  renderer: WebGLRenderer
  camera: PerspectiveCamera
  // objects: Array<Mesh>
  scene: Scene
  // selectedMeshes: Array<SelectedMeshType>

  setNeedRender: (v: boolean) => void
  setOrbit: (v: boolean) => void
  setSelectedSectionDetails: (sec: SelectedSection | undefined) => void
  setDividerPoses: (arr: DividerPos[]) => void
  setSectionPoses: (pos: SectionPos[]) => void
  setSectionProps: (prop: SectionProps) => void
  addDividers: (arr: DividerPropsType[]) => void
  getDividers: () => DividerPropsType[]
  getConfigs: () => ConfigsType

  hoveredSection: undefined | Mesh = undefined
  selectedSection: undefined | Mesh = undefined

  divider: undefined | Mesh = undefined

  constructor(
    scene: Scene,
    camera: PerspectiveCamera,
    renderer: WebGLRenderer,
    setOrbit: (v: boolean) => void,
    setNeedRender: (v: boolean) => void,
    setSelectedSection: (sec: SelectedSection | undefined) => void,
    setDividerPoses: (arr: DividerPos[]) => void,
    setSectionPoses: (pos: SectionPos[]) => void,
    setSectionProps: (prop: SectionProps) => void,
    addDividers: (arr: DividerPropsType[]) => void,
    getDividers: () => DividerPropsType[],
    getConfigs: () => ConfigsType
  ) {
    this.renderer = renderer
    this.camera = camera
    this.scene = scene
    // debugger
    this.renderer.domElement.addEventListener('mousedown', this.onMouseDown)
    this.renderer.domElement.addEventListener('touchstart', this.onTouchStart)
    this.renderer.domElement.addEventListener('touchend', this.onMouseUp)
    this.renderer.domElement.addEventListener('mouseup', this.onMouseUp)
    this.renderer.domElement.addEventListener('mousemove', this.onMouseMove)

    this.setOrbit = setOrbit
    this.setNeedRender = setNeedRender
    this.setSelectedSectionDetails = setSelectedSection
    this.setSectionPoses = setSectionPoses
    this.setDividerPoses = setDividerPoses
    this.setSectionProps = setSectionProps
    this.addDividers = addDividers
    this.getDividers = getDividers
    this.getConfigs = getConfigs
  }
  delete = () => {
    this.renderer.domElement.removeEventListener('mousedown', this.onMouseDown)
    this.renderer.domElement.removeEventListener(
      'touchstart',
      this.onTouchStart
    )
    this.renderer.domElement.removeEventListener('touchend', this.onMouseUp)
    this.renderer.domElement.removeEventListener('mouseup', this.onMouseUp)
    this.renderer.domElement.removeEventListener('mousemove', this.onMouseMove)
  }

  onTouchStart = (e: TouchEvent) => {
    if (this.renderer.domElement.parentElement) {
      this.mouse.x =
        +(
          (e.targetTouches[0].pageX -
            this.renderer.domElement.parentElement.offsetLeft) /
          this.renderer.domElement.clientWidth
        ) *
          2 -
        1
      this.mouse.y =
        -(
          (e.targetTouches[0].pageY -
            this.renderer.domElement.parentElement.offsetTop) /
          this.renderer.domElement.clientHeight
        ) *
          2 +
        1
      this.select()
    }
  }

  onMouseDown = (e: MouseEvent) => {
    if (this.renderer.domElement.parentElement) {
      this.mouse.x =
        ((e.clientX - this.renderer.domElement.parentElement.offsetLeft) /
          this.renderer.domElement.clientWidth) *
          2 -
        1
      this.mouse.y =
        -(
          (e.clientY - this.renderer.domElement.parentElement.offsetTop) /
          this.renderer.domElement.clientHeight
        ) *
          2 +
        1
      // console.log(this.mouse)

      if (!e.ctrlKey) this.select()
    }
  }

  onMouseMove = (e: MouseEvent) => {
    if (this.renderer.domElement.parentElement) {
      this.mouse.x =
        ((e.clientX - this.renderer.domElement.parentElement.offsetLeft) /
          this.renderer.domElement.clientWidth) *
          2 -
        1
      this.mouse.y =
        -(
          (e.clientY - this.renderer.domElement.parentElement.offsetTop) /
          this.renderer.domElement.clientHeight
        ) *
          2 +
        1

      this.raycater.setFromCamera(this.mouse, this.camera)

      let intersected = this.raycater.intersectObjects(
        this.scene.children.filter((child) => child.userData.type === 'section')
      )

      if (intersected.length) {
        if (this.selectedSection !== intersected[0].object)
          this.setHoveredSection(intersected[0].object as Mesh)

        if (
          this.selectedSection &&
          this.divider &&
          this.divider.userData.selectedUUID === intersected[0].object.uuid
        ) {
          this.divider.position.y = intersected[0].point.y

          this.setNeedRender(true)
        }
      } else {
        if (this.hoveredSection) this.setHoveredSection(undefined)
      }
    }
  }

  setDividerMode = (on: boolean) => {
    if (on && this.selectedSection) {
      this.divider = createDivider(
        (<PlaneGeometry>this.selectedSection.geometry).parameters.width
      )
      this.divider.position.set(
        this.selectedSection.position.x,
        this.selectedSection.position.y,
        this.selectedSection.position.z + 0.05
      )
      this.divider.name = 'divider_temp'
      this.divider.userData.selectedUUID = this.selectedSection.uuid

      const index = this.scene.children.findIndex(
        (child) => child.name === this.divider?.name
      )
      if (index === -1) this.scene.add(this.divider)
      else this.scene.children[index] = this.divider
    } else {
      const index = this.scene.children.findIndex(
        (child) => child.name === this.divider?.name
      )
      if (index !== -1) this.scene.remove(this.scene.children[index])

      this.divider = undefined
    }

    this.setNeedRender(true)
  }

  setHoveredSection = (mesh: Mesh | undefined) => {
    if (this.hoveredSection && this.hoveredSection !== this.selectedSection) {
      const material = <Material>this.hoveredSection.material
      if (!material.userData.hidden) {
        material.opacity = 0.6
        material.needsUpdate = true
      }
    }

    this.hoveredSection = mesh

    if (this.hoveredSection) {
      const material = <Material>this.hoveredSection.material

      if (!material.userData.hidden) {
        material.opacity = 0.7
        material.needsUpdate = true
      }
    }

    this.setNeedRender(true)
  }

  getDividersPoses = (dividerWidth: number) => {
    const dividerPoses = [] as DividerPos[]

    const size = new Vector2()
    this.renderer.getSize(size)

    this.scene.children.forEach((child) => {
      if (child.name.includes('divider')) {
        const point = child.clone()
        const point2 = child.clone()

        point.translateX(dividerWidth / 2)
        const rightPos = getScreenPos(
          point.position,
          this.camera,
          size.x,
          size.y
        )

        point2.translateX(-dividerWidth / 2)
        const leftPos = getScreenPos(
          point2.position,
          this.camera,
          size.x,
          size.y
        )

        dividerPoses.push({
          rightPos,
          leftPos,
          name: child.name,
        })
      }
    })

    return dividerPoses
  }

  getSectionsPoses = (sectionWidth: number, dProps?: DividerPropsType[]) => {
    const doorConfig = this.getConfigs().doorConfig
    const thickness = this.getConfigs().thickness

    const dividersProps = dProps ? dProps : this.getDividers()
    const sectionPoses = [] as SectionPos[]

    const size = new Vector2()
    this.renderer.getSize(size)

    const sectionsCount = this.scene.children.filter((child) =>
      child.name.includes('section')
    ).length

    this.scene.children.forEach((child) => {
      if (child.name.includes('section')) {
        const sectionNumber =
          child.name === 'section' ? 1 : parseInt(child.name.split('_')[1])

        const sectionType =
          sectionNumber !== 1
            ? sectionNumber === sectionsCount
              ? 'last'
              : 'middle'
            : sectionsCount === 1
            ? 'single'
            : 'first'

        const sectionHeight = parseFloat(
          (<PlaneGeometry>(<Mesh>child).geometry).parameters.height.toFixed(
            appConfig.fractionDigits
          )
        )

        let panelHeight =
          sectionHeight +
          (sectionNumber === 1
            ? dividersProps.length === 0
              ? thickness
              : dividersProps[0].type === 'visible' //if first divider is fixed shelf
              ? thickness * 1.5
              : thickness
            : 0) + //If section is the first
          (sectionNumber === dividersProps.length + 1
            ? dividersProps.length === 0
              ? thickness
              : dividersProps[dividersProps.length - 1].type === 'visible' //if last divider is fixed shelf
              ? thickness * 1.5
              : thickness
            : 0) + //If section is the last
          (sectionNumber !== 1 && sectionNumber !== dividersProps.length + 1
            ? dividersProps[sectionNumber - 2].type === 'visible' //if top divider is fixed shelf
              ? thickness / 2
              : 0
            : 0) + //If section is not first or last
          (sectionNumber !== 1 && sectionNumber !== dividersProps.length + 1
            ? dividersProps[sectionNumber - 1].type === 'visible' //if bottom divider is fixed shelf
              ? thickness / 2
              : 0
            : 0) //If section is not first or last

        panelHeight =
          panelHeight -
          (sectionType !== 'middle'
            ? sectionType === 'single'
              ? doorConfig.topReveal + doorConfig.bottomReveal
              : sectionType === 'first'
              ? doorConfig.topReveal + doorConfig.horizontalGap / 2 //first
              : doorConfig.bottomReveal + doorConfig.horizontalGap / 2 //last
            : doorConfig.horizontalGap) //middle

        const point = child.clone()
        point.translateX(-sectionWidth / 2)

        sectionPoses.push({
          ...getScreenPos(point.position, this.camera, size.x, size.y),
          height: parseFloat(sectionHeight.toFixed(appConfig.fractionDigits)),
          panelHeight: parseFloat(
            panelHeight.toFixed(appConfig.fractionDigits)
          ),
          name: child.name === 'section' ? 'section_1' : child.name,
        })
      }
    })

    return sectionPoses
  }

  checkIfSelected = (section: Section) => {
    for (const subSection of section.sections) {
      if (this.selectedSection?.name === subSection.name) {
        this.setSelectedSection(<Mesh>subSection.object)
      }
    }
  }

  setSelectedSection = (mesh: Mesh | undefined) => {
    if (this.selectedSection) {
      const material = <Material>this.selectedSection.material

      material.opacity = material.userData.hidden ? 0 : 0.6
      material.needsUpdate = true
    }

    this.selectedSection = mesh

    if (this.selectedSection) {
      //Change section view
      ;(<Material>this.selectedSection.material).opacity = 0.9
      ;(<Material>this.selectedSection.material).needsUpdate = true

      //Get 2d position of section
      const sectionWidth = (<PlaneGeometry>this.selectedSection.geometry)
        .parameters.width
      const sectionHeight = (<PlaneGeometry>this.selectedSection.geometry)
        .parameters.height

      const point = this.selectedSection.clone()
      point.translateX(sectionWidth / 2)
      point.position.y += sectionHeight / 2

      const size = new Vector2()
      this.renderer.getSize(size)

      this.setSelectedSectionDetails({
        ...getScreenPos(point.position, this.camera, size.x, size.y),
        height: sectionHeight,
        y: this.selectedSection.position.y,
        name: this.selectedSection.name,
      })

      //Get 2d positions of all sections and dividers
      // const sectionPoses = [] as SectionPos[]
      // const dividersProps = this.getDividers()

      // this.scene.children.forEach(child => {
      //     if (child.name.includes('section')) {
      //         const point = child.position.clone()
      //         point.x -= sectionWidth / 2

      //         sectionPoses.push({
      //             ...getScreenPos(point, this.camera, size.x, size.y),
      //             height: parseFloat((<PlaneGeometry>(<Mesh>child).geometry).parameters.height.toFixed(appConfig.fractionDigits)),
      //             name: child.name
      //         })
      //     }
      //     // else if (child.name.includes('divider')) {
      //     //     const point = child.position.clone()
      //     //     point.x += sectionWidth / 2

      //     //     dividerPoses.push({
      //     //         ...getScreenPos(point, this.camera, size.x, size.y),
      //     //         name: child.name
      //     //     })
      //     // }
      // })

      this.setSectionPoses(this.getSectionsPoses(sectionWidth))
      // this.setDividerPoses(dividerPoses)
    } else {
      this.setOrbit(true)
      this.setSelectedSectionDetails(undefined)
      // this.setDividerPoses([])
      this.setSectionPoses([])
    }
    this.setNeedRender(true)
  }

  select = () => {
    this.raycater.setFromCamera(this.mouse, this.camera)

    let intersected = this.raycater.intersectObjects(
      this.scene.children.filter((child) => child.userData.type === 'section')
    )

    if (intersected.length) {
      this.setOrbit(false)

      const intersectedMesh = intersected[0].object as Mesh

      if (this.selectedSection === intersectedMesh) {
        if (this.selectedSection && this.divider) {
          let height = 0

          this.scene.children.forEach((child) => {
            if (child.name.includes('section')) {
              height += (<PlaneGeometry>(<Mesh>child).geometry).parameters
                .height
            }
          })
          // debugger
          this.addDividers([
            {
              type: 'none',
              top: height / 2 - this.divider.position.y,
              uuid: v4(),
            },
          ])
          this.setDividerMode(false)
          this.setSelectedSection(undefined)
        }
      } else this.setSelectedSection(intersectedMesh)
    } else {
      // this.setSelectedSection(undefined)
      if (this.selectedSection && this.divider) {
        this.setDividerMode(false)
        // console.log(this.divider.position.y)
      }
    }

    this.setNeedRender(true)
  }

  onMouseUp = () => {
    // this.setOrbit(true)
  }
}
